import { Message } from "@/types/chat";
import { useEffect, useState } from "react";
import { ChatMessage } from "./ChatMessage";
import { TypingIndicator } from "./TypingIndicator";
import { ChevronDown } from "lucide-react";

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

export const ChatMessages = ({ messages, isLoading, messagesEndRef }: ChatMessagesProps) => {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  useEffect(() => {
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement?.parentElement;
      if (container) {
        // Smooth scroll to bottom with padding
        setTimeout(() => {
          container.scrollTo({
            top: container.scrollHeight - container.clientHeight - 32,
            behavior: 'smooth'
          });
        }, 100);

        // Show scroll indicator if content is scrollable
        const handleScroll = () => {
          const isNotAtBottom = container.scrollHeight - container.scrollTop - container.clientHeight > 100;
          setShowScrollIndicator(isNotAtBottom);
        };

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [messages, messagesEndRef]);

  const scrollToBottom = () => {
    const container = messagesEndRef.current?.parentElement?.parentElement;
    if (container) {
      container.scrollTo({
        top: container.scrollHeight - 32,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 md:p-6 pb-28 relative scroll-smooth">
      <div className="space-y-6 md:space-y-8">
        {messages.map((message) => (
          <ChatMessage 
            key={`msg-${message.id}`} 
            message={message} 
          />
        ))}

        {isLoading && (
          <div className="flex justify-start px-1">
            <div className="relative max-w-[85%] md:max-w-[80%] p-3 md:p-4 bg-muted/80 rounded-2xl rounded-bl-none backdrop-blur-sm">
              <div className="flex items-center space-x-1">
                <TypingIndicator />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} className="h-8" />
      </div>

      {showScrollIndicator && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-28 right-6 p-2.5 bg-primary/90 hover:bg-primary rounded-full shadow-lg transition-all duration-300 hover:scale-105 backdrop-blur-sm z-10 group"
          aria-label="Scroll to bottom"
        >
          <ChevronDown className="h-5 w-5 text-white transition-transform group-hover:translate-y-0.5" />
        </button>
      )}
    </div>
  );
};